import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { User, Save, AlertCircle, Send } from 'lucide-react';
import { ContactInfo } from '../services/profile-service';
import { useUpdateProfile } from '../hooks/useProfile';
import { useCreateChangeRequest } from '@/hooks/react-query/useChangeRequests';
import { useToast } from '@/hooks/useToast';
import { FieldError } from '@/components/forms/FieldError';
import { getPhoneError } from '@/utils/validation';

interface InvestorProfileViewProps {
  contactInfo: ContactInfo;
}

function getNotificationPrefs(contactInfo: ContactInfo) {
  return {
    receives_reports: contactInfo.receives_reports,
    receives_capital_calls: contactInfo.receives_capital_calls,
    receives_distributions: contactInfo.receives_distributions,
  };
}

function getPersonalInfo(contactInfo: ContactInfo) {
  return {
    first_name: contactInfo.first_name || '',
    last_name: contactInfo.last_name || '',
    phone: contactInfo.phone || '',
    mobile: contactInfo.mobile || '',
    title: contactInfo.title || '',
    department: contactInfo.department || '',
  };
}

export function InvestorProfileView({ contactInfo }: InvestorProfileViewProps) {
  const updateProfileMutation = useUpdateProfile();
  const createChangeRequestMutation = useCreateChangeRequest();
  const { toast } = useToast();

  // Fields that can be updated immediately (notification preferences only)
  const [notificationPrefs, setNotificationPrefs] = useState(() =>
    getNotificationPrefs(contactInfo)
  );

  // Fields that require approval (identity and contact information)
  const [personalInfo, setPersonalInfo] = useState(() =>
    getPersonalInfo(contactInfo)
  );

  // Reset local state when contactInfo changes from external source
  const [prevContactInfo, setPrevContactInfo] = useState(contactInfo);
  if (contactInfo !== prevContactInfo) {
    setPrevContactInfo(contactInfo);
    setNotificationPrefs(getNotificationPrefs(contactInfo));
    setPersonalInfo(getPersonalInfo(contactInfo));
  }

  const handlePersonalInfoChange = (field: string, value: string) => {
    setPersonalInfo({
      ...personalInfo,
      [field]: value,
    });
  };

  const handleNotificationChange = (field: string, value: boolean) => {
    setNotificationPrefs({
      ...notificationPrefs,
      [field]: value,
    });
  };

  const phoneError =
    personalInfo.phone === (contactInfo.phone || '')
      ? undefined
      : getPhoneError(personalInfo.phone);
  const mobileError =
    personalInfo.mobile === (contactInfo.mobile || '')
      ? undefined
      : getPhoneError(personalInfo.mobile);

  // Check if personal info has changed (requires approval)
  const hasPersonalInfoChanges = () => {
    return (
      personalInfo.first_name !== (contactInfo.first_name || '') ||
      personalInfo.last_name !== (contactInfo.last_name || '') ||
      personalInfo.phone !== (contactInfo.phone || '') ||
      personalInfo.mobile !== (contactInfo.mobile || '') ||
      personalInfo.title !== (contactInfo.title || '') ||
      personalInfo.department !== (contactInfo.department || '')
    );
  };

  // Check if notification preferences have changed (immediate update)
  const hasNotificationChanges = () => {
    return (
      notificationPrefs.receives_reports !== contactInfo.receives_reports ||
      notificationPrefs.receives_capital_calls !==
        contactInfo.receives_capital_calls ||
      notificationPrefs.receives_distributions !==
        contactInfo.receives_distributions
    );
  };

  // Success/error toasts are handled by useApiMutation (useUpdateProfile).
  const handleSaveNotifications = () => {
    updateProfileMutation.mutate(notificationPrefs);
  };

  const handleRequestPersonalInfoChange = async () => {
    if (!hasPersonalInfoChanges()) {
      toast({
        title: 'No Changes',
        description: 'No changes detected in personal information.',
        variant: 'destructive',
      });
      return;
    }

    // Build the change request with only changed fields
    const changedFields: Record<string, string> = {};
    if (personalInfo.first_name !== (contactInfo.first_name || '')) {
      changedFields.first_name = personalInfo.first_name;
    }
    if (personalInfo.last_name !== (contactInfo.last_name || '')) {
      changedFields.last_name = personalInfo.last_name;
    }
    if (personalInfo.phone !== (contactInfo.phone || '')) {
      changedFields.phone = personalInfo.phone;
    }
    if (personalInfo.mobile !== (contactInfo.mobile || '')) {
      changedFields.mobile = personalInfo.mobile;
    }
    if (personalInfo.title !== (contactInfo.title || '')) {
      changedFields.title = personalInfo.title;
    }
    if (personalInfo.department !== (contactInfo.department || '')) {
      changedFields.department = personalInfo.department;
    }

    try {
      await createChangeRequestMutation.mutateAsync({
        new_values: changedFields,
        change_reason: 'Personal information update request',
      });

      toast({
        title: 'Change Data Submitted',
        description:
          'Your personal information change data has been submitted for admin approval. You will be notified once it has been reviewed.',
      });

      // Reset form to current values
      setPersonalInfo({
        first_name: contactInfo.first_name || '',
        last_name: contactInfo.last_name || '',
        phone: contactInfo.phone || '',
        mobile: contactInfo.mobile || '',
        title: contactInfo.title || '',
        department: contactInfo.department || '',
      });
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to submit change data. Please try again.',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="space-y-6">
      {/* Personal Information - Requires Approval */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <User className="h-5 w-5 mr-2" />
            Personal Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Changes to personal information require administrator approval for
              security and compliance reasons.
            </AlertDescription>
          </Alert>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="firstName">First Name</Label>
              <Input
                id="firstName"
                value={personalInfo.first_name}
                onChange={(e) =>
                  handlePersonalInfoChange('first_name', e.target.value)
                }
              />
            </div>
            <div>
              <Label htmlFor="lastName">Last Name</Label>
              <Input
                id="lastName"
                value={personalInfo.last_name}
                onChange={(e) =>
                  handlePersonalInfoChange('last_name', e.target.value)
                }
              />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={contactInfo.email}
                disabled
                className="bg-gray-50"
              />
              <p className="text-xs text-gray-500 mt-1">
                Email cannot be changed
              </p>
            </div>
            <div>
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                type="tel"
                value={personalInfo.phone}
                onChange={(e) =>
                  handlePersonalInfoChange('phone', e.target.value)
                }
                className={phoneError ? 'border-red-500' : ''}
              />
              <FieldError message={phoneError} />
            </div>
            <div>
              <Label htmlFor="mobile">Mobile</Label>
              <Input
                id="mobile"
                type="tel"
                value={personalInfo.mobile}
                onChange={(e) =>
                  handlePersonalInfoChange('mobile', e.target.value)
                }
                className={mobileError ? 'border-red-500' : ''}
              />
              <FieldError message={mobileError} />
            </div>
            <div>
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                value={personalInfo.title}
                onChange={(e) =>
                  handlePersonalInfoChange('title', e.target.value)
                }
              />
            </div>
            <div>
              <Label htmlFor="department">Department</Label>
              <Input
                id="department"
                value={personalInfo.department}
                onChange={(e) =>
                  handlePersonalInfoChange('department', e.target.value)
                }
              />
            </div>
          </div>

          <div className="flex justify-end">
            <Button
              onClick={handleRequestPersonalInfoChange}
              disabled={
                !hasPersonalInfoChanges() ||
                createChangeRequestMutation.isPending ||
                !!phoneError ||
                !!mobileError
              }
            >
              <Send className="h-4 w-4 mr-2" />
              {createChangeRequestMutation.isPending
                ? 'Submitting...'
                : 'Request Changes'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Notification Preferences - Immediate Update */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <AlertCircle className="h-5 w-5 mr-2" />
            Notification Preferences
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-gray-500">
            Choose which communications you would like to receive. These changes
            take effect immediately.
          </p>
          <div className="flex flex-col space-y-2">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={notificationPrefs.receives_reports}
                onChange={(e) =>
                  handleNotificationChange('receives_reports', e.target.checked)
                }
                className="mr-2"
              />
              Receive Reports
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={notificationPrefs.receives_capital_calls}
                onChange={(e) =>
                  handleNotificationChange(
                    'receives_capital_calls',
                    e.target.checked
                  )
                }
                className="mr-2"
              />
              Receive Capital Calls
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={notificationPrefs.receives_distributions}
                onChange={(e) =>
                  handleNotificationChange(
                    'receives_distributions',
                    e.target.checked
                  )
                }
                className="mr-2"
              />
              Receive Distributions
            </label>
          </div>

          <div className="flex justify-end">
            <Button
              onClick={handleSaveNotifications}
              disabled={
                !hasNotificationChanges() || updateProfileMutation.isPending
              }
            >
              <Save className="h-4 w-4 mr-2" />
              {updateProfileMutation.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
